<template>
  <ActionBar>
    <Button
      variant="ghost"
      @click="props.togglePanel('eraseRestore')"
      :active="isEraseRestorePanelOpened"
      :disabled="isUiDisabled"
    >
      <template #start>
        <NewBrushIcon />
      </template>
      {{ I18n.t("editor.cutout") }}
    </Button>

    <Button
      variant="ghost"
      @click="props.togglePanel('addBackground')"
      :active="isBackgroundPanelOpened"
      :disabled="isUiDisabled"
    >
      <template #start>
        <NewBackgroundIcon />
      </template>

      {{ I18n.t("editor.background") }}
    </Button>

    <Button variant="ghost" @click="props.togglePanel('fx')" :active="isFxPanelOpened" :disabled="isUiDisabled">
      <template #start>
        <ShadowIcon />
      </template>
      {{ I18n.t("editor.apply_effects") }}
    </Button>

    <Button variant="ghost" @click="props.togglePanel('adjust')" :active="isAdjustPanelOpened" :disabled="isUiDisabled">
      <template #start>
        <AdjustIcon />
      </template>
      {{ I18n.t("editor.adjust") }}
    </Button>

    <Button variant="ghost" @click="props.togglePanel('design')" :active="isDesignPanelOpened" :disabled="isUiDisabled">
      <template #start>
        <DesignIcon />
      </template>
      {{ I18n.t("editor.design") }}
    </Button>

    <ActionBarDivider />

    <Button
      variant="ghost"
      :disabled="isUiDisabled"
      @mousedown="toggleCompareBefore"
      @mouseup="toggleCompareAfter"
      @touchstart="toggleCompareBefore"
      @touchend="toggleCompareAfter"
    >
      <template #start>
        <CompareIcon />
      </template>
    </Button>

    <!-- Undo Button -->
    <Button
      variant="ghost"
      :disabled="!props.currentPersistentStore?.canUndo"
      @click="props.currentPersistentStore?.undo"
    >
      <template #start>
        <UndoIcon />
      </template>
    </Button>

    <!-- Redo Button -->
    <Button
      variant="ghost"
      :disabled="!props.currentPersistentStore?.canRedo"
      @click="props.currentPersistentStore?.redo"
    >
      <template #start>
        <RedoIcon />
      </template>
    </Button>

    <!-- Download Menu -->
    <DownloadMenu
      :is-processing="props.isProcessing"
      :is-processing-preview-download="store.isProcessingPreviewDownload"
      :is-processing-hd-download="store.isProcessingHdDownload"
      :is-processing-magic-background-hd-download="store.isProcessingMagicBackgroundHdDownload"
      :is-hd-image-processed="store.selectedImage.hdResult?.state === ProcessingState.Finished"
      :is-generating-background="props.currentPersistentStore?.generatingBackground"
      :hd-resolution="hdResolution"
      :preview-resolution="previewResolution"
      :full-resolution="fullResolution"
      :download-hd="props.downloadHd"
      :persistent-store="props.currentPersistentStore"
      :download-preview="props.downloadPreview"
      :hd-download-enabled="hdDownloadEnabled"
      :download-magic-background-hd="props.downloadMagicBackgroundHd"
    />
  </ActionBar>
</template>

<script setup lang="ts">
import { I18n as I18nType } from "@/src/i18n";
import { computed, inject } from "vue";
import { Panel, useEditorStore } from "@/stores/editor_store";
import { PersistentStore } from "@/stores/persistent_store";
import { useToggleCompare } from "@/composables/toggle_compare";
import { ProcessingState } from "@/modules/internal_api/image";
import {
  ActionBar,
  ActionBarDivider,
  Button,
  ShadowIcon,
  CompareIcon,
  UndoIcon,
  RedoIcon,
  NewBrushIcon,
  NewBackgroundIcon,
  AdjustIcon,
  DesignIcon,
} from "prism";
import DownloadMenu from "./download_controls/download_menu.vue";

const I18n = inject<I18nType>("I18n");

interface EditorToolbarProps {
  currentPersistentStore: PersistentStore | null;
  isProcessing: boolean;
  togglePanel: (id: Panel) => void;

  // Download props
  downloadPreview: (force_share?: boolean) => Promise<void>;
  downloadHd: (closeDialog?: () => void) => Promise<void>;
  downloadMagicBackgroundHd: (generationId: string, closeDialog?: () => void) => Promise<void>;
}

const props = defineProps<EditorToolbarProps>();

const store = useEditorStore();
const { toggleCompareBefore, toggleCompareAfter } = useToggleCompare();

const isBackgroundPanelOpened = computed(() => {
  return store.openedPanel === "addBackground";
});

const isFxPanelOpened = computed(() => {
  return store.openedPanel === "fx";
});

const isEraseRestorePanelOpened = computed(() => {
  return store.openedPanel === "eraseRestore";
});

const isAdjustPanelOpened = computed(() => {
  return store.openedPanel === "adjust";
});

const isDesignPanelOpened = computed(() => {
  return store.openedPanel === "design";
});

// Download menu computed values
const previewResolution = computed(() => {
  return `${store.selectedImage.meta.previewWidth} x ${store.selectedImage.meta.previewHeight}`;
});

const hdResolution = computed(() => {
  return `${store.selectedImage.meta.hdWidth} x ${store.selectedImage.meta.hdHeight}`;
});

const fullResolution = computed(() => {
  return `${store.selectedImage.meta.fullWidth} x ${store.selectedImage.meta.fullHeight}`;
});

const hdDownloadEnabled = computed(() => {
  return store.selectedImage.meta.fullAvailable;
});

// Disable UI when processing or panel is open - for now this is current approach, will see later how the UI should behave in a new design
const isUiDisabled = computed(() => {
  return props.isProcessing;
});
</script>
